import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Star, MapPin, Clock, Shield, Award, MessageCircle, Video, Phone, Calendar, Heart } from 'lucide-react';

const CoachProfileModal = ({ isOpen, onClose, coach, onBookSession, onMessage }) => {
  const [activeTab, setActiveTab] = useState('about');

  if (!coach) return null;

  const tabs = [
    { id: 'about', name: 'About', icon: Heart },
    { id: 'reviews', name: 'Reviews', icon: Star },
    { id: 'availability', name: 'Availability', icon: Calendar }
  ];

  const mockReviews = [
    {
      id: 1,
      client: 'Sarah M.',
      rating: 5,
      date: '2 weeks ago',
      content: 'Absolutely life-changing sessions with Dr. Johnson. Her approach to self-love is gentle yet powerful. I finally understand what it means to truly accept myself.',
      verified: true
    },
    {
      id: 2,
      client: 'Emma K.',
      rating: 5,
      date: '1 month ago', 
      content: 'The best investment I\'ve made in myself. The techniques I learned have completely transformed how I talk to myself. Highly recommend!',
      verified: true
    },
    {
      id: 3,
      client: 'Michael R.',
      rating: 4,
      date: '6 weeks ago',
      content: 'Professional, empathetic, and incredibly knowledgeable. Dr. Johnson helped me work through years of self-doubt in just a few sessions.',
      verified: true
    }
  ];

  const availableSlots = [
    { day: 'Today', times: ['2:00 PM', '4:30 PM'] },
    { day: 'Tomorrow', times: ['9:00 AM', '11:00 AM', '3:00 PM', '6:00 PM'] },
    { day: 'Friday', times: ['10:00 AM', '1:00 PM', '4:00 PM'] },
    { day: 'Saturday', times: ['9:00 AM', '2:00 PM'] }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center justify-between">
            <span>Coach Profile</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Coach Header */}
          <div className="flex items-start space-x-6 p-6 border-b border-gray-200">
            <div className="relative">
              <img 
                src={coach.avatar} 
                alt={coach.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
              {coach.verified && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-800">{coach.name}</h2>
                {coach.topRated && (
                  <Badge className="bg-amber-100 text-amber-800">
                    ⭐ Top Rated Professional
                  </Badge>
                )}
                {coach.verified && (
                  <Badge className="bg-green-100 text-green-800">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              
              <p className="text-rose-600 font-medium text-lg mb-3">{coach.title}</p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 mr-2" />
                  <span className="font-medium">{coach.rating}</span>
                  <span className="text-gray-400 ml-1">({coach.reviews} reviews)</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                  <span>{coach.location}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 text-gray-400 mr-2" />
                  <span>Responds in {coach.responseTime}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Award className="w-4 h-4 text-amber-500 mr-2" />
                  <span>{coach.completedSessions} sessions completed</span>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800 mb-2">
                ${coach.hourlyRate}/hr
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={onMessage}
                  variant="outline"
                  size="sm"
                  className="border-rose-300 text-rose-600 hover:bg-rose-50"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
                <Button
                  onClick={onBookSession}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                >
                  Book Session
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-rose-500 text-rose-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'about' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">About</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">{coach.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">Specialties</h4>
                    <div className="space-y-2">
                      {coach.specialties.map((specialty, index) => (
                        <div key={specialty} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                          <span className="text-gray-700 capitalize">{specialty.replace('-', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">Session Formats</h4>
                    <div className="space-y-2">
                      {coach.formats.map((format) => {
                        const icons = {
                          video: Video,
                          phone: Phone,
                          messaging: MessageCircle,
                          'in-person': MapPin
                        };
                        const Icon = icons[format];
                        return (
                          <div key={format} className="flex items-center space-x-2">
                            <Icon className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 capitalize">{format.replace('-', ' ')}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Achievements & Certifications</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {coach.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                        <Award className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-gray-700">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Languages</h4>
                  <div className="flex space-x-2">
                    {coach.languages.map((language) => (
                      <Badge key={language} variant="outline" className="border-rose-300 text-rose-700">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                  <h4 className="font-medium text-rose-800 mb-2">🏆 Community Standing</h4>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-rose-700">Rank #{coach.communityRank} • {coach.communityBadge}</span>
                    <span className="text-rose-600">Active community participant</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Client Reviews ({coach.reviews})
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-xl font-bold text-gray-800">{coach.rating}</span>
                    <span className="text-gray-500">average</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {mockReviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                            <span className="font-medium text-rose-700">
                              {review.client.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-800">{review.client}</span>
                              {review.verified && (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  ✓ Verified Client
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                ))}
                              </div>
                              <span className="text-sm text-gray-500">{review.date}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{review.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'availability' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800">Available Time Slots</h3>
                
                <div className="grid gap-4">
                  {availableSlots.map((slot) => (
                    <div key={slot.day} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-3">{slot.day}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {slot.times.map((time) => (
                          <Button
                            key={time}
                            variant="outline"
                            size="sm"
                            className="border-rose-300 text-rose-600 hover:bg-rose-50"
                            onClick={() => {
                              onClose();
                              onBookSession();
                            }}
                          >
                            {time}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">📅 Booking Information</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Sessions are conducted securely within our platform</li>
                    <li>• Payment is processed automatically after each session</li>
                    <li>• Free cancellation up to 24 hours before your session</li>
                    <li>• All communications are monitored for quality assurance</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoachProfileModal;